import { Caption, Icon, LinkText, Logo, Paper, Paragraph, Stack } from '@mediahuis/chameleon-react';
import { ArrowBack } from '@mediahuis/chameleon-theme-wl/icons';
import React, { FC, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useBrandGroupConfig } from '../hooks/useBrandGroupConfig';
import { useBrandLogo } from '../hooks/useBrandLogo';
import { useQueryParams } from '../hooks/useQueryParams';
import { useTracking } from '../hooks/useTracking';
import { BRAND_LOGO_TYPE } from '../types/general';
import { EventActionValues, EventContextValues } from '../types/tracking';

import './brandGroupContainer.css';

interface BrandGroupContainerProps {
  children?: React.ReactNode;
}

const BrandGroupContainer: FC<BrandGroupContainerProps> = (props) => {
  const { t } = useTranslation();
  const brandGroupConfigHook = useBrandGroupConfig();
  const brandLogoHook = useBrandLogo();
  const { brand, applicationUrl, location, language } = useQueryParams();
  const { trackEventWithContext } = useTracking();

  const brandConfig = brandGroupConfigHook.getBrandConfigFromBrandId(brand);
  const brandGroupConfig = brandGroupConfigHook.getBrandGroupConfigFromBrandId(brand);
  let footerLogos = brandGroupConfig?.brands
    .filter((b) => b.designSystem.footerLogoType !== BRAND_LOGO_TYPE.NONE)
    .map((b) => brandLogoHook(b.designSystem.footerLogoType || BRAND_LOGO_TYPE.DEFAULT, b));
  if (footerLogos && brandGroupConfig?.additionalFooterLogos)
    footerLogos = footerLogos.concat(brandGroupConfig?.additionalFooterLogos);

  const backLink: string | null = useMemo(() => {
    const fallbackUrl = brandConfig?.urlDisplay ? `https://www.${brandConfig.urlDisplay}` : null;
    return location || applicationUrl || fallbackUrl;
  }, [applicationUrl, brandConfig?.urlDisplay, location]);

  return (
    <Stack
      alignItems="center"
      flexDirection="column"
      id={'container_header_wrapper'}
      className="g-6">
      <Stack
        className="container__header container__header__flex my-3 h-6"
        id="container__header"
        flexGrow={0}>
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          flexWrap="nowrap"
          flexGrow={0}
          className="container__header__flex">
          {backLink && (
            <Stack
              flexDirection="row"
              gap={0}
              alignSelf="flex-start"
              alignContent="center"
              className="container__header__link w-100">
              <Icon as={ArrowBack} className="container__header__return-link" />
              <Caption size="lg" className="my-auto">
                <LinkText
                  href={backLink}
                  onClick={() =>
                    trackEventWithContext(EventActionValues.Click, EventContextValues.Cancel)
                  }
                  className="container__header__return-link container__header__return-link--overlap"
                  id="container__header__return-link">
                  {brandConfig?.name ? (
                    <Trans
                      i18nKey="accountConsent.consent.backLink"
                      components={{ strong: <strong /> }}
                      values={{ urlDisplay: brandConfig.name }}
                    />
                  ) : (
                    <Trans
                      i18nKey="accountConsent.consent.defaultBackLink"
                      components={{ strong: <strong /> }}
                    />
                  )}
                </LinkText>
              </Caption>
            </Stack>
          )}
          <Logo
            id="container__header-logo"
            width="100px"
            {...(backLink ? {} : { className: 'm-auto' })}
          />
        </Stack>
      </Stack>
      <div className="main__content m-0">
        <Paper className="paper">
          <Stack alignItems="center" flexDirection="column" className="w-100">
            {brandLogoHook(BRAND_LOGO_TYPE.DEFAULT) && (
              <img
                alt="logo"
                data-cy={`brandImg${brand}`}
                src={brandLogoHook(brandConfig?.designSystem.logoType || BRAND_LOGO_TYPE.DEFAULT)}
                className="paper__logo"
              />
            )}
          </Stack>
          <main {...props} />
        </Paper>
      </div>
      <div className="main__footer">
        <Stack flexDirection="column">
          {footerLogos && footerLogos.length >= 1 && (
            <>
              <Paragraph
                className="paper__text--light mb-1 text-center mt-0"
                id="lng__footer-logo-text">
                <Trans i18nKey="information.loginAt" components={{ strong: <strong /> }} />
              </Paragraph>
              <Stack
                id="container__logo-array"
                alignItems="center"
                justifyContent="center"
                flexDirection="row"
                className="footer-logo-container">
                {footerLogos.map((logoUrl, i) => {
                  return (
                    <img
                      alt="brand logo"
                      key={`container__logo-array__item__${i}`}
                      src={logoUrl}
                      className="container__logo-array__item"
                    />
                  );
                })}
              </Stack>
            </>
          )}

          <LinkText
            style={{ textDecoration: 'underline' }}
            className="paper__text--light my-5 text-center"
            id="readmore__footer-text__link"
            target="blank"
            onClick={() =>
              trackEventWithContext(EventActionValues.Click, EventContextValues.ReadMore)
            }
            href={
              brandConfig?.readMore?.languages
                ? brandConfig.readMore.languages[language ?? 'nl']
                : null
            }>
            {t('information.readMore')}
          </LinkText>
        </Stack>
      </div>
    </Stack>
  );
};
export default BrandGroupContainer;
